import { PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Modal, Spin, notification } from 'antd'
import { useEffect, useState, useRef, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { checkFileSize, fallback } from '$utils'
import {
  getPartPng,
  getPartStl,
  postStl,
  getPartStlHead,
  deletePartFiles,
} from '$api/client'
import { createClientNotification } from '$api/evoAPIs'
import { useUploadLimit } from '$context/UploadLimitProvider'
import Partimage from './Partimage'
import {
  StyledStlViewer,
  StyledUpload,
  ImageContainerFallback,
  ExpandableTableCard,
  ImageContainer,
} from './styledComponents'
import { PartsTableContext } from '$components/PartsTable/PartsTableContext'
import PropTypes from 'prop-types'

const usePngPolling = (partId, onPngSuccess, onPngError) => {
  return useQuery({
    queryKey: ['partPngPoll', partId],
    queryFn: () => getPartPng(partId),
    enabled: false, // We will enable this manually
    refetchInterval: 2500, // Poll every 2.5 seconds
    refetchIntervalInBackground: true,
    retry: 120,
    retryDelay: 2500,
    onSuccess: (data) => {
      if (data) {
        onPngSuccess(data)
      }
    },
    onError: (error) => {
      onPngError(error)
    },
  })
}

const Part3dFileUpload = ({ record }) => {
  const { t, i18n } = useTranslation()

  const queryClient = useQueryClient()
  const { acquireSlot, releaseSlot, maxActiveUploads } = useUploadLimit()
  const hasAcquiredSlotRef = useRef(false)
  const pngBlobUrlRef = useRef(null)
  const stlBlobUrlRef = useRef(null)
  const lastRecordIdRef = useRef(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewImage, setPreviewImage] = useState('')
  const [previewTitle, setPreviewTitle] = useState('')
  const [fileList, setFileList] = useState([])
  const [pngImgUrl, setPngImgUrl] = useState(null)
  const [pngFallbackUrl, setPngFallbackUrl] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadingPartsPng, setLoadingPartsPng] = useState({
    isLoading: true,
    loadingText: t('part_3d_file_upload.generating_preview_png'),
  })

  const { on3DModelDelete } = useContext(PartsTableContext)

  const { refetch: startPngPolling, remove: stopPngPolling } = usePngPolling(
    record.id,
    (pngData) => {
      stopPngPolling()
      const blob = new Blob([pngData], { type: 'image/png' })
      const blobUrl = URL.createObjectURL(blob)
      setPngImgUrl(blobUrl)
      setLoadingPartsPng({ isLoading: false, loadingText: '' })
      queryClient.invalidateQueries({ queryKey: ['parts'] })
      queryClient.invalidateQueries({ queryKey: ['partDetail', record.id] })
      notification.success({ message: t('part_3d_file_upload.png_ready') })
    },
    (error) => {
      stopPngPolling()
      console.error('PNG polling failed or timed out:', error)
      setLoadingPartsPng({ isLoading: false, loadingText: '' })
      queryClient.invalidateQueries({ queryKey: ['parts'] })
      queryClient.invalidateQueries({ queryKey: ['partDetail', record.id] })
    },
  )

  const beforeUpload = (file) => checkFileSize(file.size, '2 GB', t)

  const handleNetworkError = () => {
    const lang = i18n.language === 'de' ? 'de' : 'en'
    const message =
      lang === 'de'
        ? 'Es gab ein Problem mit der Internetverbindung. Bitte versuchen Sie es in ein paar Minuten erneut.'
        : 'Something went wrong with the internet connection. Please try again in a few minutes.'

    notification.error({
      message: lang === 'de' ? 'Netzwerkfehler' : 'Network Error',
      description: message,
      duration: 10,
    })

    setLoadingPartsPng({ isLoading: false, loadingText: '' })
    setFileList([])
  }

  const handleProcessingError = async () => {
    const lang = i18n.language
    const title = t('part_3d_file_upload.error_processing_title')
    const body = t('part_3d_file_upload.file_caused_error', {
      partNo: record.id_part_client,
    })

    try {
      await createClientNotification({ title, body, lang })
    } catch (err) {
      console.error('Failed to create notification:', err)
    }

    notification.error({
      message: title,
      description: body,
      duration: 10,
    })

    setLoadingPartsPng({ isLoading: false, loadingText: '' })
    setFileList([])
    setPngImgUrl(null)
    setPngFallbackUrl(null)

    queryClient.invalidateQueries({ queryKey: ['parts'] })
    queryClient.invalidateQueries({ queryKey: ['part', record.id] })
    queryClient.invalidateQueries({ queryKey: ['partDetail', record.id] })
  }

  const deleteFilesMutation = useMutation({
    mutationFn: deletePartFiles,
    onSuccess: () => {
      setPngImgUrl(null)
      setPngFallbackUrl(null)
      setFileList([])
      if (pngBlobUrlRef.current?.url) {
        URL.revokeObjectURL(pngBlobUrlRef.current.url)
        pngBlobUrlRef.current = null
      }
      if (stlBlobUrlRef.current) {
        URL.revokeObjectURL(stlBlobUrlRef.current)
        stlBlobUrlRef.current = null
      }
      on3DModelDelete(record.id)
      queryClient.invalidateQueries({ queryKey: ['partDetail', record.id] })

      notification.success({ message: t('part_3d_file_upload.stl_removed') })
    },
    onError: (error) => {
      console.error('File deletion failed:', error)
      notification.error({
        message: t('part_3d_file_upload.stl_remove_failed'),
      })
    },
  })

  const postSTLMutation = useMutation(postStl, {
    onSuccess: async () => {
      if (hasAcquiredSlotRef.current) {
        releaseSlot()
        hasAcquiredSlotRef.current = false
      }
      setLoadingPartsPng({
        isLoading: true,
        loadingText: t('part_3d_file_upload.generating_preview_png'),
      })
      queryClient.invalidateQueries({ queryKey: ['partPngPoll', record.id] })
      startPngPolling()
    },
    onError: (error) => {
      if (hasAcquiredSlotRef.current) {
        releaseSlot()
        hasAcquiredSlotRef.current = false
      }
      console.error('Upload failed:', error)

      if (!error.response || error.response.status === 0) {
        handleNetworkError()
      } else if (error.response?.status === 503) {
        notification.warning({
          message: t(
            'part_3d_file_upload.upload_limit_title',
            'Upload Limit Reached',
          ),
          description: t(
            'part_3d_file_upload.upload_limit_description',
            'Too many files are being uploaded at the moment. Please wait and try again shortly.',
          ),
          duration: 8,
        })
        setLoadingPartsPng({ isLoading: false, loadingText: '' })
        setFileList([])
      } else {
        handleProcessingError()
      }
    },
  })

  const customUpload = ({ file, onProgress }) => {
    if (!acquireSlot()) {
      notification.warning({
        message: t(
          'part_3d_file_upload.upload_limit_title',
          'Upload Limit Reached',
        ),
        description: t(
          'part_3d_file_upload.upload_limit_description',
          `Only ${maxActiveUploads} files can be uploaded at the same time. Please wait for an upload to finish.`,
        ),
        duration: 8,
      })
      setFileList([])
      return
    }
    hasAcquiredSlotRef.current = true

    setPngImgUrl(null)
    setPngFallbackUrl(null)
    if (pngBlobUrlRef.current?.url) {
      URL.revokeObjectURL(pngBlobUrlRef.current.url)
      pngBlobUrlRef.current = null
    }
    return postSTLMutation.mutate({
      partId: record.id,
      file,
      onProgress,
    })
  }

  const deleteStl = () => {
    deleteFilesMutation.mutate(record.id)
  }

  const handleCancel = () => setPreviewOpen(false)
  const handleLoading = () => setLoading(false)

  const handlePreview = async () => {
    try {
      setLoading(true)
      setPreviewOpen(true)

      if (stlBlobUrlRef.current && lastRecordIdRef.current === record.id) {
        setPreviewImage(stlBlobUrlRef.current)
        setPreviewTitle(record.name)
        setLoading(false)
        return
      }

      if (stlBlobUrlRef.current && lastRecordIdRef.current !== record.id) {
        URL.revokeObjectURL(stlBlobUrlRef.current)
        stlBlobUrlRef.current = null
      }
      setLoading(true)

      const stlblob = await getPartStl(record.id)
      const blob = new Blob([stlblob], { type: 'model/stl' })
      const blobUrl = URL.createObjectURL(blob)
      stlBlobUrlRef.current = blobUrl
      lastRecordIdRef.current = record.id

      setPreviewImage(blobUrl)
      setPreviewTitle(record.name)
      setLoading(false)
    } catch (_error) {
      notification.error({
        message: t('preview_error'),
        description: t('preview_error_description'),
      })
    }
  }

  const handleChange = ({ fileList: newFileList }) => {
    newFileList.thumbUrl = pngImgUrl
    setFileList(newFileList)
  }

  useEffect(() => {
    let mounted = true
    let currentRecordId = record.id

    async function fetchData() {
      if (!mounted || !currentRecordId) return

      if (
        pngBlobUrlRef.current &&
        pngBlobUrlRef.current.partId === currentRecordId &&
        lastRecordIdRef.current === currentRecordId
      ) {
        setPngImgUrl(pngBlobUrlRef.current.url)
        setLoadingPartsPng({ isLoading: false, loadingText: '' })
        return
      }

      if (lastRecordIdRef.current !== currentRecordId) {
        if (pngBlobUrlRef.current?.url) {
          URL.revokeObjectURL(pngBlobUrlRef.current.url)
          pngBlobUrlRef.current = null
        }
        if (stlBlobUrlRef.current) {
          URL.revokeObjectURL(stlBlobUrlRef.current)
          stlBlobUrlRef.current = null
        }
      }

      lastRecordIdRef.current = currentRecordId

      setLoadingPartsPng({
        isLoading: true,
        loadingText: t('part_3d_file_upload.check_if_preview_png'),
      })

      try {
        const partPNG = await getPartPng(currentRecordId)

        if (!mounted || currentRecordId !== record.id) return

        const blob = new Blob([partPNG], { type: 'image/png' })
        const blobUrl = URL.createObjectURL(blob)

        pngBlobUrlRef.current = { url: blobUrl, partId: currentRecordId }

        setPngImgUrl(blobUrl)
        setLoadingPartsPng({ isLoading: false, loadingText: '' })
      } catch (_error) {
        if (!mounted || currentRecordId !== record.id) return

        try {
          await getPartStlHead(currentRecordId)
          setLoadingPartsPng({ isLoading: false, loadingText: '' })
          setPngFallbackUrl(fallback)
        } catch (_stlError) {
          setLoadingPartsPng({ isLoading: false, loadingText: '' })
        }
      }
    }

    fetchData()

    return () => {
      mounted = false
    }
  }, [record.id, record.files, t])

  useEffect(() => {
    return () => {
      if (pngBlobUrlRef.current?.url) {
        URL.revokeObjectURL(pngBlobUrlRef.current.url)
      }
      if (stlBlobUrlRef.current) {
        URL.revokeObjectURL(stlBlobUrlRef.current)
      }
    }
  }, [])

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>{t('part_3d_file_upload.upload_stl')}</div>
    </div>
  )

  return (
    <>
      {loadingPartsPng.isLoading ? (
        <ExpandableTableCard>
          <ImageContainerFallback>
            <div className="spinner">
              <Spin />
            </div>
            <div className="overlay2">{loadingPartsPng.loadingText}</div>
          </ImageContainerFallback>
        </ExpandableTableCard>
      ) : pngImgUrl || pngFallbackUrl ? (
        pngImgUrl ? (
          <div>
            <ImageContainer>
              <Partimage
                part_url={pngImgUrl}
                deleteStl={deleteStl}
                handlePreview={handlePreview}
                setFileList={setFileList}
                isDeleting={deleteFilesMutation.isLoading}
              />
            </ImageContainer>
          </div>
        ) : (
          <ExpandableTableCard>
            <ImageContainerFallback>
              <Partimage
                part_url={pngFallbackUrl}
                deleteStl={deleteStl}
                handlePreview={handlePreview}
                setFileList={setFileList}
              />
              <div className="overlay2" style={{ 'text-align': 'center' }}>
                {t('part_image_does_not_exist')}
              </div>
            </ImageContainerFallback>
          </ExpandableTableCard>
        )
      ) : (
        <ExpandableTableCard>
          <StyledUpload
            accept=".stl"
            customRequest={customUpload}
            onChange={handleChange}
            listType="picture-card"
            fileList={fileList}
            onPreview={handlePreview}
            beforeUpload={beforeUpload}
            disabled={postSTLMutation.isLoading}
          >
            {fileList.length >= 1 ? null : uploadButton}
          </StyledUpload>
        </ExpandableTableCard>
      )}

      <Modal
        open={previewOpen}
        title={previewTitle}
        footer={null}
        onCancel={handleCancel}
        width={800}
        styles={{ body: { height: '600px' } }}
        centered
        transitionName=""
        destroyOnClose={false}
      >
        {loading ? (
          <Spin
            style={{ marginTop: 290, marginLeft: 380 }}
            spinning={loading}
          />
        ) : (
          <StyledStlViewer
            key={`${previewImage}-${record.id}`}
            onFinishLoading={handleLoading}
            orbitControls
            style={{
              width: '100%',
              height: '600px',
            }}
            url={previewImage}
            modelProps={{
              scale: 1.3,
              positionX: 70,
              positionY: 70,
            }}
          />
        )}
      </Modal>
    </>
  )
}

Part3dFileUpload.propTypes = {
  record: PropTypes.shape({
    id: PropTypes.string,
    id_part_client: PropTypes.string,
    name: PropTypes.string,
    files: PropTypes.array,
  }).isRequired,
}

export default Part3dFileUpload
